import React, {
  useState,
  forwardRef,
  useCallback,
  useEffect,
  memo
} from 'react';
import { DebounceInput } from 'react-debounce-input';
import { capitalize } from 'helpers';
import styled from 'styled-components';

const MainWrapper = styled.div`
  width: 100%;
`;

const Warning = styled.span`
  display: block;
  position: relative;
  margin-top: 4px;
  float: none;
  clear: both;
  color: ${({ theme }) => theme.errorColor};
  font-size: 0.75rem;
  font-weight: 300;
  line-height: normal;
`;

const StyledLabel = styled.label`
  display: block;
  position: relative;
  line-height: normal;
  text-align: left;
  color: ${({ theme }) => theme.descriptionActions};
  font-size: 0.75rem;
  display: flex;
  flex-direction: column;
`;

const StyledFieldLabel = styled.div`
  flex-basis: 30%;
`;

const FieldAndWarningWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  > span {
    padding-left: 10px;
  }
`;

const FormField = styled.input`
  display: block;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  line-height: 40px;
  padding: 0 10px;
  margin-top: 5px;
  background: ${({ theme }) => theme.gray100};
  border: 1px solid transparent;
  color: ${({ theme, disabled }) =>
    disabled ? theme.primary100 : theme.primary400};
  font-size: 1rem;
  -webkit-border-radius: 3px;
  -moz-border-radius: 3px;
  border-radius: 3px;
  outline: none;
  border: solid 1px #e7e6ea;

  &[type='submit'] {
    display: block;
    position: relative;
    margin: 0 auto;
    width: auto;
  }
  @include single-transition(0.2s border-color ease-in);
  &:hover,
  &:focus {
    border: 1px solid darken(#eaedf0, 8%);
  }
`;

const FormFieldArea = styled.textarea`
  display: block;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  line-height: 40px;
  padding: 0 10px;
  margin-top: 5px;
  background: ${({ theme }) => theme.gray100};
  border: 1px solid transparent;
  color: ${({ theme, disabled }) =>
    disabled ? theme.primary100 : theme.primary400};
  font-size: 1rem;
  -webkit-border-radius: 3px;
  -moz-border-radius: 3px;
  border-radius: 3px;
  outline: none;
  border: solid 1px #e7e6ea;

  &[type='submit'] {
    display: block;
    position: relative;
    margin: 0 auto;
    width: auto;
  }
  transition: 0.2s border-color ease-in;
  &:hover,
  &:focus {
    border: 1px solid darken(#eaedf0, 8%);
  }
  min-height: 50px;
`;

const StyledDebounceInput = styled(DebounceInput)`
  display: block;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  line-height: 40px;
  padding: 0 10px;
  margin-top: 5px;
  color: ${({ theme, disabled }) =>
    disabled ? theme.primary100 : theme.primary400};
  background: ${({ theme }) => theme.gray100};
  border: 1px solid transparent;
  font-size: 1rem;
  -webkit-border-radius: 3px;
  -moz-border-radius: 3px;
  border-radius: 3px;
  outline: none;
  border: solid 1px #e7e6ea;

  &[type='submit'] {
    display: block;
    position: relative;
    margin: 0 auto;
    width: auto;
  }
  @include single-transition(0.2s border-color ease-in);
  &:hover,
  &:focus {
    border: 1px solid darken(#eaedf0, 8%);
  }
`;

/**
 *
InputField.defaultProps = {
  label: '',
  placeholder: '',
  value: '',
  disabled: false,
  statusMessage: '',
  statusCode: 200,
  type: 'text',
  className: null,
  handleSubmit: () => {},
  onBlur: () => {},
  hasDebounce: false,
  numLines: 1,
  isRequired: false,
  dataTestId: null,
  alt: false
};
 */

const InputField = forwardRef(
  (
    {
      childKey,
      dataTestId,
      className,
      disabled,
      fieldName,
      hasDebounce,
      isRequired,
      onFieldChange,
      handleSubmit,
      onBlur,
      label,
      numLines,
      placeholder,
      statusCode,
      statusMessage,
      type,
      value,
      maxLength,
      debounceTimeout
    },
    ref
  ) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
      if (value !== localValue) {
        setLocalValue(value);
      }
    }, [value, localValue]);

    // === CALLBACKS ===
    const handleFieldChange = useCallback(
      (e) => {
        if (maxLength && e.target.value.length > maxLength) return;
        if (!disabled) {
          onFieldChange(fieldName, e.target.value);
          setLocalValue(e.target.value);
        }
      },
      [maxLength, disabled, onFieldChange, fieldName]
    );

    const onFieldBlur = (e) => {
      if (!disabled) {
        onBlur(fieldName, e.target.value);
        setLocalValue(e.target.value);
      }
    };

    const onSubmit = (e) => {
      if (e.key === 'Enter') {
        handleSubmit();
      }
    };

    const createContent = (warning) => {
      // fill out off the props and render the correct type of input
      const formFieldProps = {
        disabled,
        id: fieldName,
        key: childKey,
        name: fieldName,
        onChange: handleFieldChange,
        onKeyPress: onSubmit,
        onBlur: onFieldBlur,
        placeholder,
        type,
        value: localValue
      };

      if (hasDebounce) {
        formFieldProps.debounceTimeout = debounceTimeout ?? 500;
        formFieldProps.minLength = 1;
      }

      const formField = hasDebounce ? (
        <StyledDebounceInput {...formFieldProps} inputRef={ref} />
      ) : numLines > 1 ? (
        <FormFieldArea {...formFieldProps} rows={numLines} ref={ref} />
      ) : (
        <FormField {...formFieldProps} ref={ref} />
      );

      let content = formField;

      // If a label is supplied it needs to wrap Around the formField to satisfy jsx-a11y
      if (label) {
        content = (
          <StyledLabel htmlFor={fieldName}>
            <StyledFieldLabel>{`${capitalize(label)} ${
              isRequired ? '*' : ''
            }`}</StyledFieldLabel>
            <FieldAndWarningWrapper>
              {formField}
              {warning}
            </FieldAndWarningWrapper>
          </StyledLabel>
        );
      }

      return content;
    };

    let warning;
    if (statusCode >= 300 && statusMessage && statusMessage !== '') {
      warning = <Warning>{statusMessage}</Warning>;
    }

    return (
      <MainWrapper className={className} data-testid={dataTestId}>
        {createContent(warning)}
      </MainWrapper>
    );
  }
);

export default memo(InputField);
